import { APIProvider } from "../../AppController";
import { APIRoutes } from "../../config";
import type { NeoShareState } from "../../hexagon/core/ports/driven/neoProjectRepository";

/**
 * Direct API client for the **Neo Conversations** surface — the only Neo
 * feature with live customers. Newer Neo surfaces (projects, agents, tickets,
 * workflows) go through the hexagon (`neoProjectController`,
 * `neoTicketController`, `neoWorkflowController`); this thin module is kept for
 * the shipped conversation flows that have not yet been migrated.
 */

const fillRoute = (
  template: string,
  params: Record<string, string | number>
) => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    // Refuse to interpolate an unresolved id: ``String(undefined)`` would
    // emit the literal "undefined" into the path, which the backend rejects
    // with an ``int_parsing`` 422. Fail loudly instead of sending it.
    if (value === undefined || value === null || value === "") {
      throw new Error(
        `fillRoute: missing value for path param "${key}" in "${template}"`
      );
    }
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

export const forkNeoConversation = async (
  conversationId: number,
  body?: { title?: string; fork_reason?: string; workflow_id?: number }
): Promise<{
  id: number;
  project_id?: number | null;
  title: string | null;
  locked?: number;
  extra_info?: Record<string, unknown> | null;
}> => {
  return (await APIProvider.POST(
    fillRoute(APIRoutes.apiNeoConversationFork, {
      conversation_id: conversationId,
    }),
    body ?? {}
  )) as {
    id: number;
    project_id?: number | null;
    title: string | null;
    locked?: number;
    extra_info?: Record<string, unknown> | null;
  };
};

export interface VoiceCallTranscriptTurn {
  role?: string;
  message: string;
  t_secs?: number | null;
}

export interface VoiceCallTranscriptResponse {
  conversation_id: string;
  ready: boolean;
  status?: string | null;
  ended?: boolean;
  duration_secs?: number | null;
  transcript_summary?: string | null;
  call_successful?: string | null;
  message_count?: number;
  transcript?: VoiceCallTranscriptTurn[];
  audio_available?: boolean;
}

export interface VoiceCallAudioResponse {
  conversation_id: string;
  ready: boolean;
  url?: string;
}

/** Fetch the stored, clinic-gated transcript for a Neo voice call. */
export const getVoiceCallTranscript = async (
  conversationId: string
): Promise<VoiceCallTranscriptResponse> => {
  return (await APIProvider.GET(
    fillRoute(APIRoutes.apiVoiceCallTranscript, {
      conversation_id: conversationId,
    }),
    {}
  )) as VoiceCallTranscriptResponse;
};

/** Fetch a short-lived presigned URL for a Neo voice call's audio (clinic-gated). */
export const getVoiceCallAudio = async (
  conversationId: string
): Promise<VoiceCallAudioResponse> => {
  return (await APIProvider.GET(
    fillRoute(APIRoutes.apiVoiceCallAudio, {
      conversation_id: conversationId,
    }),
    {}
  )) as VoiceCallAudioResponse;
};

export const getNeoConversationShare = async (
  conversationId: number
): Promise<NeoShareState> => {
  return (await APIProvider.GET(
    fillRoute(APIRoutes.apiNeoConversationShare, {
      conversation_id: conversationId,
    }),
    {}
  )) as NeoShareState;
};

export const setNeoConversationShareClinics = async (
  conversationId: number,
  clinicIds: number[]
): Promise<NeoShareState> => {
  return (await APIProvider.POST(
    fillRoute(APIRoutes.apiNeoConversationShare, {
      conversation_id: conversationId,
    }),
    { clinic_ids: clinicIds }
  )) as NeoShareState;
};
